import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { format } from 'date-fns';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loading, Pagination, SortIcon, DeleteDialog } from '@/components/common';
import { useAuthStore } from '@/features/auth/stores/auth.store';

import { employeesApi } from '../api/clients.api';
import { EmployeeActionDialog } from './EmployeeActionDialog';
import type { Employee } from '../types/client.types';

interface EmployeeListProps {
  clientId: number;
}

interface EmployeeFilters {
  page: number;
  limit: number;
  orderBy: string;
  order: 'asc' | 'desc' | '';
}

export function EmployeeList({ clientId }: EmployeeListProps) {
  const { t } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);

  const [filters, setFilters] = useState<EmployeeFilters>({
    page: 0,
    limit: 10,
    orderBy: 'createdAt',
    order: 'desc',
  });

  const updateFilter = <K extends keyof EmployeeFilters>(key: K, value: EmployeeFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['employees', clientId, filters],
    queryFn: () => employeesApi.getByClientId(clientId),
    enabled: !!clientId && hasAccess('employee', 'get'),
  });

  const handleSort = (field: string) => {
    if (filters.orderBy !== field) {
      setFilters((prev) => ({ ...prev, orderBy: field, order: 'asc', page: 0 }));
    } else {
      if (filters.order === 'asc') {
        setFilters((prev) => ({ ...prev, order: 'desc', page: 0 }));
      } else if (filters.order === 'desc') {
        setFilters((prev) => ({ ...prev, orderBy: '', order: '', page: 0 }));
      } else {
        setFilters((prev) => ({ ...prev, order: 'asc', page: 0 }));
      }
    }
  };

  const columns: ColumnDef<Employee>[] = [
    ...(hasAccess('employee', 'update') || hasAccess('employee', 'delete')
      ? [
          {
            id: 'actions',
            header: () => t('actions.actions'),
            cell: ({ row }: { row: { original: Employee } }) => {
              const employee = row.original;
              return (
                <div className="flex gap-0">
                  {hasAccess('employee', 'update') && (
                    <EmployeeActionDialog
                      id={employee.id}
                      clientId={clientId}
                      onSuccess={() => refetch()}
                    />
                  )}
                  {hasAccess('employee', 'delete') && (
                    <DeleteDialog
                      id={employee.id}
                      title={employee.name}
                      url="employees"
                      queryKey={['employees', clientId]}
                    />
                  )}
                </div>
              );
            },
          } as ColumnDef<Employee>,
        ]
      : []),
    {
      accessorKey: 'name',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => handleSort('name')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleSort('name')}
        >
          <p>{t('name')}</p>
          <SortIcon active={filters.orderBy === 'name'} direction={filters.order} />
        </div>
      ),
      cell: ({ row }) => <div>{row.original.name}</div>,
    },
    {
      accessorKey: 'role',
      header: () => <p>{t('role')}</p>,
      cell: ({ row }) => <div>{row.original.role || row.original.position || '-'}</div>,
    },
    {
      accessorKey: 'email',
      header: () => <p>{t('email')}</p>,
      cell: ({ row }) => <div>{row.original.email || '-'}</div>,
    },
    {
      accessorKey: 'phone',
      header: () => <p>{t('phone')}</p>,
      cell: ({ row }) => {
        const phone = row.original.phone;
        if (!phone) return <div>-</div>;
        // Format phone for display
        const digits = phone.replace(/\D/g, '');
        if (digits.length === 12) {
          return (
            <div>
              +{digits.slice(0, 3)} ({digits.slice(3, 5)}) {digits.slice(5, 8)} {digits.slice(8, 10)}{' '}
              {digits.slice(10, 12)}
            </div>
          );
        }
        return <div>{phone}</div>;
      },
    },
    {
      accessorKey: 'createdAt',
      header: () => (
        <div
          className="flex items-center cursor-pointer select-none"
          role="button"
          tabIndex={0}
          onClick={() => handleSort('createdAt')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleSort('createdAt')}
        >
          <p>{t('createdAt')}</p>
          <SortIcon active={filters.orderBy === 'createdAt'} direction={filters.order} />
        </div>
      ),
      cell: ({ row }) => {
        const dateStr = row.original.createdAt;
        if (!dateStr) return <div>-</div>;
        return <div>{format(new Date(dateStr), 'yyyy-MM-dd HH:mm')}</div>;
      },
    },
  ];

  const employees = data?.results || [];
  const total = data?.meta?.total || employees.length;

  const table = useReactTable({
    data: employees,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="mt-6">
      <div className="flex py-2 px-4 justify-between items-center border-b">
        <h1 className="text-xl font-semibold">{t('employees')}</h1>
        <div className="flex items-center gap-3">
          {hasAccess('employee', 'create') && (
            <EmployeeActionDialog clientId={clientId} onSuccess={() => refetch()} />
          )}
        </div>
      </div>

      <div className="px-4">
        <div className="border rounded-md mt-4">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <Loading className="w-full" />
                  </TableCell>
                </TableRow>
              ) : employees.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    <p className="text-muted-foreground">{t('noEmployeesFound')}</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <Pagination
          page={filters.page}
          limit={filters.limit}
          total={total}
          onPageChange={(val) => updateFilter('page', val)}
          onLimitChange={(val) => setFilters((prev) => ({ ...prev, limit: val, page: 0 }))}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
